import { PureComponent, ReactNode } from 'react';
import { Appointment } from '../../api/fetchAppointmentsV3';
import ResourceAvatar from './ResourceAvatar';
import { crossIcon, moreOptionsIcon, tagsIcon } from '../../components/common/Icons';
import { Menu, notification, Popover, Tag } from 'antd';
import cancelAppointment from '../../api/cancelAppointment';
import { PatientProfileCardGlobalSearch } from '../../components/globalsearch/GlobalSearchModal';
import { Patient, toLocalPatient } from '../../store/types/patient';
import { getPatientByLocalId } from '../../store/idbHandler';
import checkinAppointment from '../../api/checkinAppointment';
import endAppointment from '../../api/endAppointment';
import { track } from '../../tracking';
import { TagsEntity } from '../../api/fetchQueueConfig';
import { getQueueConfig } from '../../store/localstorage';
import { getPxPrintUrl } from '../utils';
import AntButton from '../../components/prescriptionPad/rightPanel/workspace/padV2/AntButton';

type Props = {
    apt?: Appointment & { isLocalPresnt: boolean; localRxId?: string };
    resource: { title: string; extendedProps: { doc: { personal: { pic?: string } } } };
    closeMe: (shouldRefresh?: boolean) => void;
    onAppointmentAction: () => void;
    goToPatientDetails: (patient: Patient) => void;
};

type State = {
    selectedPatient?: Patient;
};

class AppointmentSelectedCard extends PureComponent<Props, State> {
    tagsMap: Map<string | number, TagsEntity> = new Map();

    state: State = {
        selectedPatient: this.props.apt?.patient
            ? toLocalPatient(this.props.apt?.patient, this.props.apt?.formData || [])
            : undefined,
    };

    async componentDidMount(): Promise<void> {
        const { apt } = this.props;
        if (!apt || !apt.patient_oid) return;

        const pt = await getPatientByLocalId(apt.patient_oid);
        if (pt) {
            this.setState({ selectedPatient: pt });
        }

        const tags = getQueueConfig().tags || [];
        tags.forEach((tag) => {
            this.tagsMap.set(tag.tag_id, tag);
        });
    }

    cancelAppointmentAction = (): void => {
        track('CANCEL_APPOINTMENT');
        const { aid } = this.props.apt || {};
        if (!aid) return;

        cancelAppointment(aid)
            .then(() => {
                notification.success({
                    message: 'Appointment cancelled',
                });
                this.props.closeMe(true);
            })
            .catch((e) => {
                console.error(e);
                notification.error({
                    message: 'Error in cancelling appointment',
                });
            });
    };

    checkInAppointment = (): void => {
        track('CHECKIN_APPOINTMENT');
        const { apt } = this.props;
        if (!apt || !apt.aid) return;

        checkinAppointment(apt.aid)
            .then(() => {
                notification.success({
                    message: 'Appointment checked in',
                });
                this.props.closeMe(true);
            })
            .catch((e) => {
                console.error(e);
                notification.error({
                    message: 'Error in checking in appointment',
                });
            });
    };

    endAppointmentAction = (): void => {
        track('END_APPOINTMENT');
        const { apt } = this.props;
        if (!apt || !apt.aid) return;

        endAppointment({
            status: 'CMNP',
            visitId: apt.aid,
            docId: apt.doctor_oid,
            ptOid: apt.patient_oid || '',
            startDateTime: apt.start_time,
            endDateTime: new Date().toISOString(),
            clinicId: apt.clinic_id,
        })
            .then(() => {
                notification.success({
                    message: 'Appointment ended',
                });
                this.props.closeMe(true);
            })
            .catch((e) => {
                console.error(e);
                notification.error({
                    message: 'Error in ending appointment',
                });
            });
    };

    onTagClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        track('TAG_CLICK');
    };

    render(): ReactNode {
        const { resource, closeMe, apt, onAppointmentAction, goToPatientDetails } = this.props;
        const { selectedPatient } = this.state;

        if (!apt || !apt.patient_oid) return null;

        return (
            <div
                className="bg-white rounded-12 space-y-8"
                style={{ boxShadow: '0px 2px 16px 0px rgba(0, 0, 0, 0.30)', pointerEvents: 'auto' }}
                // onClick={(e) => {
                //     e.preventDefault();
                //     e.stopPropagation();
                // }}
            >
                <div className="">
                    <div className="flex justify-end p-16">
                        <span onClick={() => closeMe()}>
                            {crossIcon('cursor-pointer w-16 h-16 fill-neutral-6')}
                        </span>
                    </div>
                    {selectedPatient && (
                        <PatientProfileCardGlobalSearch
                            selectedPatient={selectedPatient}
                            goToPatientDetails={() => goToPatientDetails(selectedPatient)}
                            border={false}
                        />
                    )}
                    {apt.tags.length ? (
                        <div className="flex items-center">
                            <span className="px-16">{tagsIcon()}</span>
                            <div className="py-8 space-y-8 space-x-8">
                                {apt.tags.map((t) => (
                                    <Tag
                                        className="bg-darwin-neutral-50 py-4 px-8 border-darwin-neutral-50 rounded-4 text-12 font-400"
                                        onClick={this.onTagClick}
                                    >
                                        {this.tagsMap.get(t)?.label}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div
                    style={{ boxShadow: '0px -2px 8px 0px rgba(0, 0, 0, 0.08)' }}
                    className="p-16 space-y-12"
                >
                    <div className="flex space-x-8 items-center">
                        <ResourceAvatar resource={resource} size={36} />
                        <div className="flex items-center">
                            <span className="font-700 text-13">{resource.title}</span>
                        </div>
                    </div>
                    <div className="flex justify-between w-full items-center">
                        <div className="flex space-x-12">
                            {apt.status === 'BK' ? (
                                <AntButton
                                    onClick={this.checkInAppointment}
                                    type="primary"
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="1em"
                                            viewBox="0 0 640 512"
                                            fill="currentColor"
                                        >
                                            <path d="M208 96a48 48 0 1 0 0-96 48 48 0 1 0 0 96zM141.8 266.6l29.9-89.8c7.7 1.2 15 3.6 21.7 7.1c-.1 .3-.2 .7-.3 1L160.7 288.6l-16.4-13.3c-2.6-2.1-3.6-5.6-2.5-8.7zm59 54.6l28.5-91.3 10.5 36.7c1.9 6.5 5.4 12.5 10.2 17.3L279 313c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-27.6-27.6-10.9-38.1C260.1 162.8 213.9 128 161.3 128h-4.9c-16.1 0-32.1 2.6-47.4 7.7C69.1 149 36.6 178.5 19.5 217l-9.5 21.3c-5.4 12.1 .1 26.3 12.2 31.7s26.3-.1 31.7-12.2l9.5-21.3C74.3 212 94.3 192.9 119 183.1L96.2 251.4c-7.4 22.1-.3 46.5 17.8 61.2l104.4 84.8 22.1 96c3 12.9 15.9 21 28.8 18s21-15.9 18-28.8L264.6 384c-2-8.7-6.8-16.4-13.8-22.1l-50.1-40.7zm-101.1 21L75.5 402.5 7 471c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l69.7-69.7c3.8-3.8 6.8-8.4 8.9-13.4l19.2-48-39-31.7zM416 24c0-13.3-10.7-24-24-24s-24 10.7-24 24V72c0 13.3 10.7 24 24 24s24-10.7 24-24V24zM553 159c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l39 39H392c-13.3 0-24 10.7-24 24s10.7 24 24 24H558.1l-39 39c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l80-80c9.4-9.4 9.4-24.6 0-33.9l-80-80zM392 128c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24zm24 216c0-13.3-10.7-24-24-24s-24 10.7-24 24v16c0 13.3 10.7 24 24 24s24-10.7 24-24V344zm-24 72c-13.3 0-24 10.7-24 24v48c0 13.3 10.7 24 24 24s24-10.7 24-24V440c0-13.3-10.7-24-24-24z"></path>
                                        </svg>
                                    }
                                >
                                    Check In
                                </AntButton>
                            ) : apt.status === 'CM' ? (
                                <AntButton
                                    onClick={() =>
                                        apt.prescription_id
                                            ? window.open(
                                                  getPxPrintUrl(apt.prescription_id),
                                                  '_blank',
                                              )
                                            : null
                                    }
                                    type="primary"
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="1em"
                                            viewBox="0 0 448 512"
                                        >
                                            <path
                                                d="M32 0C14.3 0 0 14.3 0 32V192v96c0 17.7 14.3 32 32 32s32-14.3 32-32V224h50.7l128 128L137.4 457.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L288 397.3 393.4 502.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L333.3 352 438.6 246.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 306.7l-85.8-85.8C251.4 209.1 288 164.8 288 112C288 50.1 237.9 0 176 0H32zM176 160H64V64H176c26.5 0 48 21.5 48 48s-21.5 48-48 48z"
                                                fill="currentColor"
                                            ></path>
                                        </svg>
                                    }
                                >
                                    Print
                                </AntButton>
                            ) : apt.status !== 'CMNP' ? (
                                <AntButton
                                    onClick={onAppointmentAction}
                                    type="primary"
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="1em"
                                            viewBox="0 0 448 512"
                                        >
                                            <path
                                                d="M32 0C14.3 0 0 14.3 0 32V192v96c0 17.7 14.3 32 32 32s32-14.3 32-32V224h50.7l128 128L137.4 457.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L288 397.3 393.4 502.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L333.3 352 438.6 246.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 306.7l-85.8-85.8C251.4 209.1 288 164.8 288 112C288 50.1 237.9 0 176 0H32zM176 160H64V64H176c26.5 0 48 21.5 48 48s-21.5 48-48 48z"
                                                fill="currentColor"
                                            ></path>
                                        </svg>
                                    }
                                >
                                    {apt.stage === 'ONGOING' ? 'Resume Visit' : 'Start Visit'}
                                </AntButton>
                            ) : null}
                            {!['CM', 'CMNP', 'BK'].includes(apt.status) && (
                                <AntButton
                                    onClick={this.endAppointmentAction}
                                    className="flex items-center space-x-8"
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    type="danger"
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="1em"
                                            viewBox="0 0 512 512"
                                        >
                                            <path
                                                d="M505 273c9.4-9.4 9.4-24.6 0-33.9L377 111c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l87 87L184 232c-13.3 0-24 10.7-24 24s10.7 24 24 24l246.1 0-87 87c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0L505 273zM168 80c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 32C39.4 32 0 71.4 0 120L0 392c0 48.6 39.4 88 88 88l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-80 0c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l80 0z"
                                                fill="currentColor"
                                            ></path>
                                        </svg>
                                    }
                                >
                                    Exit
                                </AntButton>
                            )}
                        </div>
                        {!['CM', 'CMNP'].includes(apt.status) && (
                            <Popover
                                // visible
                                content={
                                    <Menu>
                                        <Menu.Item
                                            onClick={this.cancelAppointmentAction}
                                            className="flex items-center"
                                            icon={
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    height="1em"
                                                    viewBox="0 0 512 512"
                                                    className="my-2 w-13 h-14 dr-max-w-13 dr-max-h-13"
                                                >
                                                    <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM175 175c-9.4 9.4-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0z"></path>
                                                </svg>
                                            }
                                        >
                                            Cancel
                                        </Menu.Item>
                                    </Menu>
                                }
                                getPopupContainer={(trigger) =>
                                    trigger.parentElement || document.body
                                }
                            >
                                {moreOptionsIcon('w-16 h-16 fill-neutral-6 cursor-pointer')}
                            </Popover>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default AppointmentSelectedCard;
